import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import Swal from 'sweetalert2';
import HttpStatus from 'http-status-codes';
import SvgPage from './SvgPage';
import PleaseTiltDevice from './Tilt';
import Overlay from './Overlay';
import ItemCard from './ItemCard';
import { getCatalog, getItems, checkout } from './../../api/api';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import 'react-image-lightbox/style.css'; // This only needs to be imported once in your app
import '../../css/Catalog.css';
import { motion } from 'framer-motion';
import Cart from './Cart';
import {Snackbar} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import useCart from '../../util/cart';
import Overview from './Overview';
import Intro from './Intro';
import { isIOS, isMobileSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

const VERTICAL = 0;
const VERTICAL_UPSIDE_DOWN = 180;
const CAT = { pages: [], chapters: [] };

const variants = {
	visible: { opacity: 1, transition: { ease: 'easeIn' } },
	hidden: { opacity: 0 }
};

function useTilt() {
	const [tilt, setTilt] = useState(0);
	React.useEffect(() => {
		const handleTilt = () => setTilt(window.orientation);
		handleTilt();
		window.addEventListener('orientationchange', handleTilt);
		return () => window.removeEventListener('orientationchange', handleTilt);
	}, []);
	return tilt === VERTICAL || tilt === VERTICAL_UPSIDE_DOWN;
}

async function fetchCatalog(customer, catalog) {
	const c = await getCatalog(customer, catalog);
	const chapters = [];
	c.pages.forEach((page, index) => {
		if(page.chapter && page.chapter !== chapters[chapters.length - 1]?.name) {
			chapters.push({ name: page.chapter, index });
		}
	});
	return { ...c, minDelivery: new Date(c.minDelivery), maxDelivery: new Date(c.maxDelivery), chapters };
}

const Catalog = () => {
	const handle = useFullScreenHandle();

	//hooks
	const scrollDownRef = useRef(null);
	const [invalidtoken, setInvalidToken] = useState(false);
	const [openFinshedOrder, setOpenFinishedOrder] = React.useState(false);
	const params = useParams();
	const page = +params.page || 0;
	const history = useHistory();
	const [pageSkus, setPageSkus] = useState({});
	const [item, setItem] = useState(null);
	const [showItem, setShowItem] = useState(false);
	const [openCart, setOpenCart] = useState(false);
	const [catalog, setCatalog] = useState(CAT);
	const [showThumbnails, setShowThumbnails] = useState(false);
	const shouldTilt = useTilt();

	const { cart, setCartItem, clearCart } = useCart(params.customer, params.catalog, catalog.code);
	const { t } = useTranslation();

	const load = async (p, index) => {
		const img = new Image();
		const src = p.imageBig;
		img.onload = () => {
			const lazy = {
				height: img.naturalHeight,
				width: img.naturalWidth,
				src
			};
			setCatalog(cat => {
				const clone = Object.assign({}, cat);
				clone.pages = cat.pages.slice();
				clone.pages[index] = Object.assign({ lazy }, p);
				return clone;
			});
		};
		img.src = src;

		// loading all skus on current page
		if (!p.mappings || p.mappings && !p.mappings.length) return;
		const currentPageSkus = p.mappings.map(mapping => mapping.item);
		const items = await getItems(params.customer, params.catalog, currentPageSkus).catch(e => void console.log(e));
		if(items) {
			setPageSkus(prev => ({...prev, [index]: items.reduce((acc, val) => {
				acc[val.sku] = val;
				return acc;
			}, {})}));
		}
	};

	// effects

	// get catalogs
	useEffect(() => {
		fetchCatalog(params.customer, params.catalog).then(setCatalog).catch(err => {
			setInvalidToken(true);
			console.error('Failed to load catalogs', err);
		});
	}, [params.customer, params.catalog]);

	// initial load
	useEffect(() => {
		if (catalog?.pages?.[page - 1] && !pageSkus[page - 1]) {
			const p = catalog.pages[page - 1];
			if (p && !p.lazy) {
				load(p, page - 1);
			}
		}
	}, [catalog]);

	const handleCloseFinishedOrder = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}

		setOpenFinishedOrder(false);
	};

	// event handlers
	const onChange = async (index) => {
		history.push(`/${params.customer}/${params.catalog}/${index}`);
		// should history push but please keep token
		if (catalog?.pages?.[index] && !pageSkus[index]) {
			const p = catalog.pages[index];
			if (p && !p.lazy) {
				load(p, index);
			}
		}
	};

	const createOrder = async (details, deliveryDate) => {
		const data = {
			...details, deliveryDate
		};
		data.lines = cart.lines.map(line => {
			return {
				item: line.item.sku,
				quantity: line.quantity
			};
		});
		await checkout(params.customer, params.catalog, data);
		clearCart();
		setOpenCart();
		setOpenFinishedOrder(true);
	};

	const callItem = async (sku) => {
		try {
			setShowItem(true);
			setItem(null);
			setItem(pageSkus[page - 1][sku]);
		} catch (err) {
			setShowItem(false);
			if (err.response?.status === HttpStatus.NOT_FOUND) {
				Swal.fire({
					icon: 'error',
					title: t('oops'),
					text: t('itemNotFound')
				});
			} else {
				const text = err.response?.data?.message || err.message;
				Swal.fire({
					icon: 'error',
					title: t('oops'),
					text
				});
			}
		}
	};

	const go = (forward) => {
		if (forward && page < catalog.pages.length) {
			onChange(page + 1);
		} else if (!forward && page - 1 >= 0) {
			onChange(page - 1);
		}
	};

	const enterFullScreenIos = () => {
		window.scrollTo(0, 1);
		scrollDownRef.current.scrollIntoView();
	};

	if (invalidtoken) {
		return <div className='invalid-token'>
			Invalid token
		</div>;
	}

	if (shouldTilt || isIOS && !isMobileSafari) {
		return <PleaseTiltDevice shouldTilt={shouldTilt} shouldUseSafari={isIOS && !isMobileSafari} />;
	}

	return <>
		<Snackbar
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right'
			}}
			open={openFinshedOrder}
			autoHideDuration={5000}
			onClose={handleCloseFinishedOrder}
			message={<div>{t('thanksForYourOrder', { customer: catalog.customer })}</div>}
			action={
				<React.Fragment>
					<IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseFinishedOrder}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</React.Fragment>
			}
		/>
		<FullScreen handle={handle}><div className='catalog'>
			{page > 0 && <>
				<Carousel
					infinite
					showStatus={false}
					showIndicators={false}
					showThumbs={false}
					showArrows={false}
					selectedItem={page - 1}
					swipeable={false}
				>
					{
						catalog.pages.map((p, index) => {
							return <SvgPage key={index}
								index={index}
								page={page - 1}
								{...p.lazy}
								onMappingClick={callItem}
								mappings={p.mappings}
								pageSkus={pageSkus[page - 1]}
							/>;
						})
					}
				</Carousel>
				<motion.div
					style={openCart ? {} : { 'pointerEvents': 'none' }}
					initial="hidden"
					animate={openCart ? 'visible' : 'hidden'}
					variants={variants}
				>
					<Cart cart={cart}
						friends={catalog.friends}
						setCartItem={setCartItem}
						minDelivery={catalog.minDelivery}
						maxDelivery={catalog.maxDelivery}
						additionalWeeks={catalog.additionalWeeks}
						createOrder={createOrder} />
				</motion.div>
				<motion.div
					style={showItem ? {} : { 'pointerEvents': 'none' }}
					initial="hidden"
					animate={showItem ? 'visible' : 'hidden'}
					variants={variants}
				>
					<ItemCard
						open={showItem}
						addCartItem={(i, q) => {
							if(q > 0) {
								const line = item && cart.lines.find(l => l.item.sku === item.sku);
								const currentAmount = line?.quantity || 0;
								const maxQuantity = line?.item.availability || Infinity;
								setCartItem(i, Math.min(q + currentAmount, maxQuantity));
							}
							setShowItem();
						}}
						item={item} />
				</motion.div>
				<motion.div
					style={showThumbnails ? {} : { 'pointerEvents': 'none' }}
					initial="hidden"
					animate={showThumbnails ? 'visible' : 'hidden'}
					variants={variants}
				>
					<Overview pages={catalog.pages} page={page - 1} open={showThumbnails} onChange={e => { onChange(e); setShowThumbnails(false); }} chapters={catalog.chapters} />
				</motion.div>
			</>}
			{page === 0 && <Intro customer={catalog.customer} />}
			<Overlay
				customer={catalog.customer}
				arrowsOnly={page < 1}
				enterFullScreen={handle.enter}
				enterFullScreenIos={enterFullScreenIos}
				cart={cart.lines}
				openCart={() => setOpenCart(true)}
				overviewOpened={showThumbnails || showItem || openCart}
				go={go}
				openThumbnails={() => setShowThumbnails(true)}
				onClose={() => {
					if (showThumbnails) {
						setShowThumbnails(false);
					} else {
						setOpenCart(false);
						setShowItem(false);
					}
				}}
			/>
		</div>
		</FullScreen>
		<div ref={scrollDownRef} className='scroll-down-ref' />
	</>;
};

export default Catalog;
